<template>
  <div
    ref="headline"
    class="w-full flex gap-1 items-center group/headline"
    :class="{ '-ms-5': closable && editable }"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @click="touched = true">
    <div
      v-if="closable && editable"
      class="flex items-center justify-center cursor-pointer rounded-full p-1 bg-secondary-200 hover:bg-secondary-300 invisible group-hover/headline:visible"
      @click="emit('hide')">
      <X class="w-3 h-3 stroke-[1.2px]" />
    </div>
    <div
      class="headline w-full h-full flex items-between gap-2"
      :class="{
        'p-1 border border-secondary-250 rounded-[4px]':
          visualBoundary === 'border',
        'bg-secondary-100': !editable && visualBoundary === 'border',
        '!border-primary': isFocus && visualBoundary === 'border',
        'headline-outline': visualBoundary === 'outline',
        'headline-none': visualBoundary === 'none',
        'outline-primary-400 outline-dashed outline-2 outline-offset-1;':
          isFocus && visualBoundary === 'outline',
        [mergedOptions.maxLines ? 'limited-lines' : '']: true,
      }"
      :style="headlineStyle">
      <ReadonlyTiptapContent
        v-if="!editable || !touched"
        v-model="displayModel"
        v-tooltip.top="tooltipOptions"
        :placeholder="placeholder"
        :options="mergedOptions"
        class="readonly-tiptap"
        :extensions="extensions"/>
      <TiptapText
        v-else-if="touched"
        :id="id"
        ref="tiptapTextRef"
        :key="editorKey"
        v-model="model"
        :text="text"
        :editable="editable"
        menu-position="hover"
        :keyboard-settings="mergedOptions.keyboardSettings"
        :preview="false"
        :zoom="mergedOptions.zoom"
        :placeholder="placeholder"
        :extensions="extensions"
        :allow-enter="mergedOptions.allowEnter"
        class="min-h-6 tiptap-editor"
        @keyup.enter="emit('keyup.enter')"
        @blur="onBlur"
        @focus="onFocus"
        @click-outside="touched = false"
        @update:model-value="onUpdate"/>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  TiptapText,
  textAreaExtensionSupport,
  ReadonlyTiptapContent,
} from '@amit/tiptap';
import { X } from 'lucide-vue-next';
import type { PropType } from 'vue';
import { computed, inject, ref, watch } from 'vue';
import { parseTooltip } from '@amit/tiptap';
import type { HeadlineOptions } from './Headline.options';
import { createDefaultOptions } from './Headline.options';

type BorderStyle = 'border' | 'outline' | 'none';

const props = defineProps({
  id: { type: String, default: 'header' },
  modelValue: { type: String, default: '' },
  closable: { type: Boolean, default: true },
  editable: { type: Boolean, default: true },
  visualBoundary: { type: String as PropType<BorderStyle>, default: 'outline' },
  text: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  options: {
    type: Object as PropType<Partial<HeadlineOptions>>,
    default: () => ({}),
  },
  locale: { type: String, default: 'ltr' },
});
const direction = inject('direction', 'rtl');
const model = defineModel<string>();
const displayModel = computed(() => model.value ?? props.text);
const emit = defineEmits(['hide', 'keyup.enter', 'blur', 'focus', 'update']);

const isMathCourse = inject<boolean>('isMathCourse', false);
const headline = ref<HTMLElement | null>(null);
const touched = ref(false);
const isHovered = ref(false);
const isFocus = ref(false);
const tiptapTextRef = ref();

const defaultOptions = createDefaultOptions(isMathCourse);

const mergedOptions = computed(() => ({ ...defaultOptions, ...props.options }));

const shouldDisable = (node: string) =>
  mergedOptions.value.defaultNode === node ||
  mergedOptions.value.defaultNode === 'headline';

const baseAttributes = {
  fontSize: mergedOptions.value.fontSize,
  textAlign: mergedOptions.value.textAlign,
  class: '!my-0.5 !py-0',
};

const extensions = computed(() => ({
  ...textAreaExtensionSupport,
  document: { defaultNode: mergedOptions.value.defaultNode },
  paragraph: shouldDisable('heading')
    ? false
    : { HTMLAttributes: baseAttributes },
  heading: shouldDisable('paragraph')
    ? false
    : { HTMLAttributes: baseAttributes, level: mergedOptions.value.level },
  headline: {
    HTMLAttributes: {
      maxLength: mergedOptions.value.maxLength,
      sizeBreakpoint: mergedOptions.value.sizeBreakpoint,
      textAlign: mergedOptions.value.textAlign,
      lineHeight: mergedOptions.value.lineHeight,
      fontSize: mergedOptions.value.fontSize,
    },
  },
  hide_katex: isMathCourse,
}));

const showTooltip = computed(() => {
  if (!headline.value && !isHovered.value) return true;

  const proseMirrorEl = headline.value?.querySelector('.ProseMirror');
  if (proseMirrorEl && isHovered.value) {
    const style = window.getComputedStyle(proseMirrorEl);
    const lineHeight = parseFloat(style.lineHeight);
    const height = proseMirrorEl.scrollHeight;
    const numberOfLines = Math.round(height / lineHeight);
    if (
      mergedOptions.value.maxLines != null &&
      numberOfLines > mergedOptions.value.maxLines
    ) {
      return true;
    }
  }
  return false;
});
const tooltipOptions = computed(() => {
  if (!showTooltip.value) return null;
  return {
    value: `<div dir="${direction}">${parseTooltip(displayModel.value, {
      fontSize: '12px',
      inverted: true,
      locale: direction,
    })}</div>`,
    escape: false,
    class: '!z-[1300] max-w-[200px] tiptap-tooltip text-center',
  };
});

const onFocus = () => {
  isFocus.value = true;
  emit('focus');
};

const onBlur = () => {
  isFocus.value = false;
  emit('blur');
};

const onUpdate = () => {
  emit('update');
};

const editorKey = computed(() =>
  JSON.stringify({
    mergedOptions: mergedOptions.value,
    placeholder: props.placeholder,
    textAlign: mergedOptions.value.textAlign,
  }),
);

watch(tiptapTextRef, val => {
  if (val?.editor) {
    const position = mergedOptions.value.maxLines ? 'start' : 'end';
    val.editor.commands.focus(position);
  }
});

const headlineStyle = computed(() => {
  const maxLines = mergedOptions.value.maxLines;
  const fontSize = mergedOptions.value.fontSize || '18px';
  const lineHeight = mergedOptions.value.lineHeight;
  const pxFontSize = parseInt(fontSize);
  let style: Record<string, string | number> = {
    '--line-clamp': maxLines ?? 'unset',
  };
  if (maxLines && pxFontSize) {
    style.maxHeight = `${Math.round(pxFontSize * lineHeight * maxLines)}px`;
  }
  return style;
});
</script>

<style scoped>
:deep(.tiptap.ProseMirror),
:deep(.editor-content) {
  min-height: auto;
  padding-block: 0px;
  @apply px-0;
}

:deep(.tiptap.ProseMirror) p:last-child,
:deep(.tiptap.ProseMirror) h3:last-child {
  @apply my-1.5;
}

:deep(.tiptap.ProseMirror) h3:last-child {
  @apply mb-0;
}

:deep(.preview-wrapper) {
  @apply border-0;
}

.headline:has(:deep(.editor-content) + *) :deep(.editor-content) {
  @apply w-[90%];
}

.headline :deep(.tiptap-menu-item) {
  @apply w-5 h-5 flex justify-center items-center rounded-sm border border-secondary-900 bg-secondary-50 overflow-hidden bg-white/70;
}

.limited-lines .tiptap-editor {
  overflow: hidden;
  height: unset;
  max-height: 100%;
  min-height: unset;
}

.limited-lines .tiptap-editor *:not(.font-katex):not(.font-katex *) {
  height: inherit;
}

.limited-lines :deep(.editor-content.readonly-html) {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--line-clamp);
  white-space: normal;
  max-height: 100%;
}

.limited-lines .readonly-tiptap :deep(p),
.limited-lines .readonly-tiptap :deep(p *) {
  font-size: inherit !important;
  line-height: inherit !important;
}
</style>
